/**
 * Marketplace listing copy — turn captured lot data into clean, ready-to-paste
 * listing content (title, suggested price, description paragraphs).
 *
 * Used by the portal for the Facebook Marketplace copy kit; the API keeps a
 * logic copy for eBay draft generation (services/api/src/lib/marketplace/
 * listingCopy.ts — keep the two in sync).
 */

export interface ListingCopyInput {
  /** Raw lot title as captured from the auction site. */
  title?: string | null;
  /** Raw lot description text (may contain HTML fragments/entities). */
  description?: string | null;
  /** User/AI low resale estimate. */
  lowEstimate?: number | null;
  /** User/AI high resale estimate. */
  highEstimate?: number | null;
  /** Median of sold eBay comps when a price analysis ran. */
  compsMedian?: number | null;
  /** Number of comps behind the median (display hint). */
  compsCount?: number | null;
}

export type ListingPriceSource = 'comps' | 'estimate' | 'none';

export interface ListingBasics {
  /** Cleaned full title. */
  title: string;
  /** Title truncated at a word boundary to eBay's 80-char limit. */
  ebayTitle: string;
  /** Suggested asking price, or null when nothing is known. */
  price: number | null;
  priceSource: ListingPriceSource;
  /** Plain-text description paragraphs. */
  paragraphs: string[];
}

const EBAY_TITLE_MAX = 80;
const DESCRIPTION_MAX_CHARS = 3500;

/** Strip tags, decode common entities, and collapse whitespace runs. */
function toPlainText(value: string): string {
  return value
    .replace(/<br\s*\/?\s*>/gi, '\n')
    .replace(/<\/(p|div|li|tr|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/[ \t]+/g, ' ');
}

/** Strip auction-house prefixes like "Lot 123:" / "LOT #45 -" from a title. */
function stripLotPrefix(title: string): string {
  return title.replace(/^\s*lot\s*#?\s*[\w-]+\s*[:\-–—]\s*/i, '').trim();
}

/** Soften an all-caps title into word-capitalized text (marketplaces penalize shouting). */
function softenAllCaps(title: string): string {
  const letters = title.replace(/[^a-zA-Z]/g, '');
  if (!letters || letters !== letters.toUpperCase()) return title;
  return title
    .toLowerCase()
    .replace(/(^|[\s\-/("'])([a-z])/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

/** Truncate at a word boundary, never mid-word unless the first word overflows. */
export function truncateAtWord(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const slice = text.slice(0, maxLength + 1);
  const lastSpace = slice.lastIndexOf(' ');
  if (lastSpace > maxLength * 0.5) {
    return slice.slice(0, lastSpace).trim();
  }
  return text.slice(0, maxLength).trim();
}

/** Clean a raw auction title for marketplace use. */
export function cleanListingTitle(raw: string | null | undefined): string {
  if (typeof raw !== 'string') return '';
  const collapsed = toPlainText(raw).replace(/\s+/g, ' ').trim();
  return softenAllCaps(stripLotPrefix(collapsed));
}

function isPositive(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Suggested asking price: sold-comps median when available (real market
 * signal), else the midpoint of the resale estimates. Whole dollars at $10+.
 */
export function suggestListingPrice(input: ListingCopyInput): {
  price: number | null;
  priceSource: ListingPriceSource;
} {
  let price: number | null = null;
  let priceSource: ListingPriceSource = 'none';

  if (isPositive(input.compsMedian)) {
    price = input.compsMedian;
    priceSource = 'comps';
  } else if (isPositive(input.lowEstimate) && isPositive(input.highEstimate)) {
    price = (input.lowEstimate + input.highEstimate) / 2;
    priceSource = 'estimate';
  } else if (isPositive(input.highEstimate) || isPositive(input.lowEstimate)) {
    price = (isPositive(input.highEstimate) ? input.highEstimate : input.lowEstimate) as number;
    priceSource = 'estimate';
  }

  if (price == null) return { price: null, priceSource: 'none' };
  const rounded = price >= 10 ? Math.round(price) : Math.round(price * 100) / 100;
  return { price: rounded, priceSource };
}

/** Split a raw description into clean plain-text paragraphs, capped in size. */
export function buildDescriptionParagraphs(raw: string | null | undefined): string[] {
  if (typeof raw !== 'string' || !raw.trim()) return [];
  const text = toPlainText(raw);
  const paragraphs: string[] = [];
  let total = 0;
  for (const part of text.split(/\n+/)) {
    const trimmed = part.replace(/\s+/g, ' ').trim();
    if (!trimmed) continue;
    if (total + trimmed.length > DESCRIPTION_MAX_CHARS) {
      const remaining = DESCRIPTION_MAX_CHARS - total;
      if (remaining > 40) paragraphs.push(truncateAtWord(trimmed, remaining) + '…');
      break;
    }
    paragraphs.push(trimmed);
    total += trimmed.length;
  }
  return paragraphs;
}

/** Build the marketplace-neutral listing basics from captured lot data. */
export function buildListingBasics(input: ListingCopyInput): ListingBasics {
  const title = cleanListingTitle(input.title);
  const { price, priceSource } = suggestListingPrice(input);
  return {
    title,
    ebayTitle: truncateAtWord(title, EBAY_TITLE_MAX),
    price,
    priceSource,
    paragraphs: buildDescriptionParagraphs(input.description),
  };
}

/** Format a price for copy text ($1,234 or $7.50). */
function formatPrice(price: number): string {
  const hasCents = Math.round(price * 100) % 100 !== 0;
  return (
    '$' +
    price.toLocaleString('en-US', {
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    })
  );
}

/**
 * Facebook Marketplace has no public listing API, so we generate a paste-ready
 * text block instead (title, price, description).
 */
export function buildFacebookCopy(basics: ListingBasics): string {
  const lines: string[] = [];
  if (basics.title) lines.push(basics.title);
  if (basics.price != null) {
    lines.push(
      `Price: ${formatPrice(basics.price)}` +
        (basics.priceSource === 'comps' ? ' (based on recent sold prices)' : '')
    );
  }
  if (basics.paragraphs.length) {
    lines.push('');
    lines.push(basics.paragraphs.join('\n\n'));
  }
  return lines.join('\n').trim();
}
